<template>
  <div
    v-if="show"
    :class="embedded ? 'embedded-docs-panel' : 'modal-overlay'"
    @click.self="handleOverlayClick"
  >
    <div class="docs-modal-container" @click.stop>

      <!-- Header -->
      <div class="docs-modal-header">
        <div class="header-left">
          <i class="fas fa-folder-open"></i>
          <div class="header-text">
            <h2>Documents &amp; Analysis</h2>
            <p v-if="!embedded" class="header-subtitle">{{ entityName || 'Entity' }}</p>
          </div>
        </div>
        <button v-if="!embedded" class="modal-close" @click="$emit('close')">&times;</button>
      </div>

      <!-- Loading skeleton -->
      <div v-if="loading" class="docs-modal-body loading-body">
        <div class="skeleton-list">
          <div v-for="n in 4" :key="n" class="skeleton-row" />
        </div>
      </div>

      <!-- Error -->
      <div v-else-if="error" class="docs-modal-body error-body">
        <i class="fas fa-exclamation-triangle"></i>
        <p>{{ error }}</p>
        <button class="btn btn-secondary" @click="loadDocuments">
          <i class="fas fa-redo"></i> Retry
        </button>
      </div>

      <!-- Empty state -->
      <div v-else-if="documents.length === 0" class="docs-modal-body empty-body">
        <i class="fas fa-file-circle-question"></i>
        <h3>No documents analyzed yet</h3>
        <p>Once documents are uploaded and analyzed for this entity, they will appear here.</p>
      </div>

      <!-- 3-panel content -->
      <div v-else class="docs-modal-body three-panel-body">

        <!-- Panel 1: Document list -->
        <div class="panel panel-docs">
          <div class="panel-header">
            <i class="fas fa-file-alt"></i>
            <span>Documents</span>
            <span class="panel-count">{{ documents.length }}</span>
          </div>
          <div class="panel-scroll">
            <div
              v-for="doc in documents"
              :key="doc.id"
              class="doc-item"
              :class="{ selected: selectedDoc && selectedDoc.id === doc.id }"
              @click="selectDoc(doc)"
            >
              <div class="doc-item-top">
                <span class="doc-name" :title="doc.name">
                  <i class="fas fa-file-pdf doc-icon"></i>
                  {{ truncateName(doc.name) }}
                </span>
                <span v-if="doc.is_current" class="badge badge-current">Current</span>
                <span v-else-if="documents.length > 1" class="badge badge-superseded">Superseded</span>
              </div>
              <div class="doc-item-meta">
                <span class="doc-date">
                  <i class="fas fa-calendar-alt"></i>
                  {{ formatDate(doc.latest_analysis_date) }}
                </span>
                <span
                  v-if="latestCheckRun(doc)"
                  class="doc-score-pill"
                  :class="getScorePillClass(latestCheckRun(doc))"
                >
                  {{ latestCheckRun(doc).passed }}/{{ latestCheckRun(doc).total }} passed
                </span>
              </div>
              <div v-if="doc.coverage_types && doc.coverage_types.length" class="doc-coverage-chips">
                <span
                  v-for="ct in doc.coverage_types"
                  :key="ct"
                  class="coverage-chip"
                >{{ ct }}</span>
              </div>
            </div>
          </div>
        </div>

        <!-- Panel 2: Analysis session history for selected doc -->
        <div class="panel panel-sessions">
          <div class="panel-header">
            <i class="fas fa-history"></i>
            <span>Analysis History</span>
            <span v-if="selectedDoc" class="panel-count">{{ selectedDoc.check_runs.length }}</span>
          </div>

          <div v-if="!selectedDoc" class="panel-empty">
            <i class="fas fa-hand-point-left"></i>
            <p>Select a document to view its analysis history</p>
          </div>

          <div v-else class="panel-scroll">
            <div
              v-for="run in sortedCheckRuns"
              :key="run.check_run_id"
              class="session-item"
              :class="{ selected: selectedRun && selectedRun.check_run_id === run.check_run_id }"
              @click="selectRun(run)"
            >
              <div class="session-item-top">
                <span class="session-date">{{ formatDate(run.analysis_date) }}</span>
                <span class="session-status-badge" :class="`status-${run.status.toLowerCase()}`">
                  {{ run.status }}
                </span>
              </div>
              <div v-if="run.check_definition_name" class="session-def-name">
                {{ run.check_definition_name }}
              </div>
              <div class="session-summary">
                <span class="summary-pill passed">
                  <i class="fas fa-check-circle"></i> {{ run.passed }} passed
                </span>
                <span class="summary-pill failed">
                  <i class="fas fa-times-circle"></i> {{ run.failed }} failed
                </span>
                <span v-if="run.total > 0" class="summary-total">/ {{ run.total }}</span>
              </div>
            </div>
          </div>
        </div>

        <!-- Panel 3: Check run detail (PDF + results) -->
        <div class="panel panel-detail">
          <div class="panel-header">
            <i class="fas fa-clipboard-check"></i>
            <span>Analysis Results</span>
          </div>

          <!-- Nothing selected -->
          <div v-if="!selectedRun" class="panel-empty">
            <i class="fas fa-hand-point-left"></i>
            <p>Select an analysis to view the full results</p>
          </div>

          <!-- Loading run detail -->
          <div v-else-if="runDetailLoading" class="panel-loading">
            <i class="fas fa-spinner fa-spin"></i>
            <span>Loading results…</span>
          </div>

          <!-- Run detail error -->
          <div v-else-if="runDetailError" class="panel-empty error-state">
            <i class="fas fa-exclamation-triangle"></i>
            <p>{{ runDetailError }}</p>
            <button class="btn btn-sm btn-secondary" @click="loadRunDetail(selectedRun)">
              <i class="fas fa-redo"></i> Retry
            </button>
          </div>

          <!-- Run detail content -->
          <div v-else-if="runDetail" class="detail-content">

            <!-- Summary bar -->
            <div class="detail-summary-bar">
              <div class="summary-card">
                <div class="card-label">Passed</div>
                <div class="card-value passed">{{ runDetail.results_summary?.passed || selectedRun.passed || 0 }}</div>
              </div>
              <div class="summary-card">
                <div class="card-label">Failed</div>
                <div class="card-value failed">{{ runDetail.results_summary?.failed || selectedRun.failed || 0 }}</div>
              </div>
              <div class="summary-card">
                <div class="card-label">Total</div>
                <div class="card-value">{{ runDetail.results_summary?.total || selectedRun.total || 0 }}</div>
              </div>
              <div v-if="runDetail.status" class="summary-card">
                <div class="card-label">Status</div>
                <div class="card-value" :class="`status-${runDetail.status.toLowerCase()}`">{{ runDetail.status }}</div>
              </div>
              <div class="summary-actions">
                <a
                  v-if="selectedDocFileUrl"
                  :href="selectedDocFileUrl"
                  target="_blank"
                  class="btn btn-sm btn-outline"
                  title="Open document in new tab"
                >
                  <i class="fas fa-external-link-alt"></i> Full Doc
                </a>
              </div>
            </div>

            <!-- Split: PDF viewer + requirement results -->
            <div class="detail-split">

              <!-- PDF viewer -->
              <div class="detail-pdf-pane">
                <div class="pane-label">
                  <i class="fas fa-file-pdf"></i> Source Document
                </div>
                <div v-if="selectedDocFileUrl && isPdf(selectedDoc)" class="pdf-wrapper">
                  <iframe
                    :src="selectedDocFileUrl"
                    type="application/pdf"
                    class="pdf-iframe"
                    title="Document Preview"
                  />
                </div>
                <div v-else class="pdf-placeholder">
                  <i class="fas fa-file-alt"></i>
                  <p>{{ selectedDocFileUrl ? 'Preview not available for this file type' : 'No document file available' }}</p>
                  <a
                    v-if="selectedDocFileUrl"
                    :href="selectedDocFileUrl"
                    target="_blank"
                    class="btn btn-sm btn-outline"
                  >
                    <i class="fas fa-download"></i> Download to view
                  </a>
                </div>
              </div>

              <!-- Requirement results -->
              <div class="detail-results-pane">
                <div class="pane-label">
                  <i class="fas fa-list-check"></i> Requirement Results
                </div>

                <div v-if="requirementResultsLoading" class="results-loading">
                  <i class="fas fa-spinner fa-spin"></i> Loading requirements…
                </div>

                <div v-else-if="requirementResults.length === 0" class="results-empty">
                  No requirement results found for this analysis.
                </div>

                <div v-else class="results-list">
                  <!-- Failed first -->
                  <div v-if="failedResults.length" class="results-section">
                    <div class="results-section-header failed-header">
                      <i class="fas fa-times-circle"></i>
                      Issues Found ({{ failedResults.length }})
                    </div>
                    <div
                      v-for="result in failedResults"
                      :key="result.id"
                      class="req-result-item failed-item"
                    >
                      <div class="req-result-top">
                        <span class="req-code">{{ result.requirement_code }}</span>
                        <span
                          v-if="result.requirement_severity"
                          class="severity-badge"
                          :class="`sev-${result.requirement_severity.toLowerCase()}`"
                        >{{ result.requirement_severity }}</span>
                      </div>
                      <div class="req-result-name">{{ result.requirement_name }}</div>
                      <div v-if="getEvidenceText(result)" class="req-evidence">
                        <strong>Evidence:</strong> {{ getEvidenceText(result) }}
                      </div>
                    </div>
                  </div>

                  <!-- Passed -->
                  <div v-if="passedResults.length" class="results-section">
                    <div
                      class="results-section-header passed-header"
                      @click="showPassed = !showPassed"
                      style="cursor:pointer"
                    >
                      <i class="fas fa-check-circle"></i>
                      Passed ({{ passedResults.length }})
                      <i :class="showPassed ? 'fas fa-chevron-up' : 'fas fa-chevron-down'" style="margin-left:auto" />
                    </div>
                    <template v-if="showPassed">
                      <div
                        v-for="result in passedResults"
                        :key="result.id"
                        class="req-result-item passed-item"
                      >
                        <div class="req-result-top">
                          <span class="req-code">{{ result.requirement_code }}</span>
                        </div>
                        <div class="req-result-name">{{ result.requirement_name }}</div>
                      </div>
                    </template>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

      </div>
    </div>
  </div>
</template>

<script>
import api from '@/services/api';

export default {
  name: 'EntityDocumentsModal',

  props: {
    show: { type: Boolean, default: false },
    embedded: { type: Boolean, default: false },
    subjectId: { type: [Number, String], default: null },
    entityName: { type: String, default: '' },
  },

  emits: ['close'],

  data() {
    return {
      loading: false,
      error: null,
      documents: [],

      selectedDoc: null,
      selectedRun: null,

      runDetailLoading: false,
      runDetailError: null,
      runDetail: null,

      requirementResultsLoading: false,
      requirementResults: [],

      showPassed: false,
      documentsRequest: 0,
    };
  },

  computed: {
    sortedCheckRuns() {
      if (!this.selectedDoc) return [];
      return [...(this.selectedDoc.check_runs || [])].sort(
        (a, b) => (b.analysis_date || '').localeCompare(a.analysis_date || '')
      );
    },

    selectedDocFileUrl() {
      if (!this.selectedDoc) return null;
      return this.selectedDoc.file_url || this.selectedDoc.file || null;
    },

    failedResults() {
      return this.requirementResults.filter(r => r.passed === false || r.passed === 0);
    },

    passedResults() {
      return this.requirementResults.filter(r => r.passed === true || r.passed === 1);
    },
  },

  mounted() {
    // The entity record embeds this panel with show already true. A watcher
    // on show never fires in that case, so the Documents tab stayed empty
    // without calling the API.
    this.loadIfReady();
  },

  watch: {
    show(val) {
      if (val) this.loadIfReady();
    },
    subjectId() {
      if (this.show) this.loadIfReady();
    },
  },

  methods: {
    handleOverlayClick() {
      if (!this.embedded) {
        this.$emit('close');
      }
    },

    reset() {
      this.documents = [];
      this.error = null;
      this.selectedDoc = null;
      this.selectedRun = null;
      this.runDetail = null;
      this.runDetailError = null;
      this.requirementResults = [];
      this.showPassed = false;
    },

    loadIfReady() {
      if (!this.show || !this.subjectId) return;
      this.reset();
      this.loadDocuments();
    },

    async loadDocuments() {
      const requestId = ++this.documentsRequest;
      this.loading = true;
      this.error = null;
      try {
        const res = await api.get(`/compliance-subjects/${this.subjectId}/documents-with-sessions/`);
        if (this.documentsRequest !== requestId) return;
        this.documents = res.data.documents || [];

        // Auto-select the first (most recent / current) document
        if (this.documents.length) {
          const current = this.documents.find(d => d.is_current) || this.documents[0];
          this.selectDoc(current);
        }
      } catch (err) {
        if (this.documentsRequest !== requestId) return;
        this.error = err.response?.data?.error || 'Failed to load documents. Please try again.';
      } finally {
        if (this.documentsRequest === requestId) this.loading = false;
      }
    },

    selectDoc(doc) {
      this.selectedDoc = doc;
      this.selectedRun = null;
      this.runDetail = null;
      this.runDetailError = null;
      this.requirementResults = [];
      this.showPassed = false;

      // Auto-select the most recent check run
      if (doc.check_runs && doc.check_runs.length) {
        const sorted = [...doc.check_runs].sort(
          (a, b) => (b.analysis_date || '').localeCompare(a.analysis_date || '')
        );
        this.selectRun(sorted[0]);
      }
    },

    async selectRun(run) {
      this.selectedRun = run;
      this.runDetail = null;
      this.runDetailError = null;
      this.requirementResults = [];
      this.showPassed = false;
      await this.loadRunDetail(run);
    },

    async loadRunDetail(run) {
      this.runDetailLoading = true;
      this.runDetailError = null;
      try {
        const [runRes, reqRes] = await Promise.all([
          api.get(`/comprehensive-check-runs/${run.check_run_id}/`),
          api.get(`/requirement-results/?check_run_id=${run.check_run_id}`),
        ]);
        this.runDetail = runRes.data;
        this.requirementResults = reqRes.data.results || reqRes.data || [];
      } catch (err) {
        this.runDetailError = err.response?.data?.error || 'Failed to load analysis results.';
      } finally {
        this.runDetailLoading = false;
      }
    },

    latestCheckRun(doc) {
      if (!doc.check_runs || !doc.check_runs.length) return null;
      return [...doc.check_runs].sort(
        (a, b) => (b.analysis_date || '').localeCompare(a.analysis_date || '')
      )[0];
    },

    isPdf(doc) {
      if (!doc) return false;
      const name = (doc.name || '').toLowerCase();
      return name.endsWith('.pdf');
    },

    truncateName(name) {
      if (!name) return 'Unnamed document';
      if (name.length <= 30) return name;
      const ext = name.lastIndexOf('.');
      if (ext > 0 && name.length - ext <= 5) {
        return name.substring(0, 25) + '…' + name.substring(ext);
      }
      return name.substring(0, 27) + '…';
    },

    formatDate(dateStr) {
      if (!dateStr) return '—';
      try {
        return new Date(dateStr).toLocaleDateString('en-US', {
          year: 'numeric', month: 'short', day: 'numeric',
        });
      } catch {
        return dateStr;
      }
    },

    getScorePillClass(run) {
      if (!run || run.total === 0) return 'score-neutral';
      const pct = (run.passed / run.total) * 100;
      if (pct >= 80) return 'score-good';
      if (pct >= 50) return 'score-warn';
      return 'score-bad';
    },

    getEvidenceText(result) {
      if (!result) return '';
      if (result.evidence) return result.evidence;
      if (result.results?.evidence) return result.results.evidence;
      if (result.check_results?.evidence) return result.check_results.evidence;
      return '';
    },
  },
};
</script>

<style scoped>
/* ── Overlay & container ── */
.modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.55);
  z-index: 1100;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1rem;
}

.docs-modal-container {
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.25);
  width: 95vw;
  max-width: 1400px;
  height: 88vh;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

/* ── Header ── */
.docs-modal-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1rem 1.25rem;
  border-bottom: 1px solid #e5e7eb;
  flex-shrink: 0;
  background: #f9fafb;
}

.header-left {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.header-left .fas {
  font-size: 1.25rem;
  color: #6366f1;
}

.header-text h2 {
  margin: 0;
  font-size: 1.1rem;
  font-weight: 700;
  color: #1f2937;
}

.header-subtitle {
  margin: 0;
  font-size: 0.8rem;
  color: #6b7280;
}

.modal-close {
  background: none;
  border: none;
  font-size: 1.5rem;
  line-height: 1;
  color: #6b7280;
  cursor: pointer;
  padding: 0.25rem 0.5rem;
  border-radius: 4px;
}
.modal-close:hover { background: #f3f4f6; color: #1f2937; }

/* ── Body wrappers ── */
.docs-modal-body {
  flex: 1;
  overflow: hidden;
}

.loading-body,
.error-body,
.empty-body {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 1rem;
  height: 100%;
  color: #6b7280;
  font-size: 0.95rem;
}

.loading-body .fas, .error-body .fas, .empty-body .fas {
  font-size: 2.5rem;
  color: #d1d5db;
}

.skeleton-list { display: flex; flex-direction: column; gap: 0.75rem; width: 60%; }
.skeleton-row {
  height: 56px;
  border-radius: 8px;
  background: linear-gradient(90deg, #f3f4f6 25%, #e5e7eb 50%, #f3f4f6 75%);
  background-size: 200% 100%;
  animation: shimmer 1.4s infinite;
}
@keyframes shimmer { 0%{background-position:200% 0} 100%{background-position:-200% 0} }

/* ── Three-panel layout ── */
.three-panel-body {
  display: flex;
  height: 100%;
}

.panel {
  display: flex;
  flex-direction: column;
  border-right: 1px solid #e5e7eb;
  overflow: hidden;
}

.panel:last-child { border-right: none; }

.panel-docs    { width: 22%; min-width: 200px; }
.panel-sessions { width: 28%; min-width: 220px; }
.panel-detail  { flex: 1; min-width: 0; }

.panel-header {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.625rem 0.875rem;
  background: #f8fafc;
  border-bottom: 1px solid #e5e7eb;
  font-size: 0.8rem;
  font-weight: 600;
  color: #374151;
  flex-shrink: 0;
}

.panel-header .fas { color: #6366f1; }

.panel-count {
  margin-left: auto;
  background: #e0e7ff;
  color: #4338ca;
  font-size: 0.7rem;
  font-weight: 700;
  padding: 0.1rem 0.4rem;
  border-radius: 10px;
}

.panel-scroll {
  flex: 1;
  overflow-y: auto;
  padding: 0.5rem;
}

.panel-empty {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  color: #9ca3af;
  font-size: 0.85rem;
  padding: 1.5rem;
  text-align: center;
}
.panel-empty .fas { font-size: 1.75rem; color: #d1d5db; }

.panel-loading {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  color: #6b7280;
}

/* ── Document list items ── */
.doc-item {
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  padding: 0.625rem 0.75rem;
  margin-bottom: 0.4rem;
  cursor: pointer;
  transition: all 0.15s;
}

.doc-item:hover { border-color: #6366f1; background: #f5f3ff; }
.doc-item.selected { border-color: #6366f1; background: #ede9fe; }

.doc-item-top {
  display: flex;
  align-items: flex-start;
  gap: 0.4rem;
  justify-content: space-between;
  margin-bottom: 0.3rem;
}

.doc-name {
  font-size: 0.78rem;
  font-weight: 600;
  color: #1f2937;
  word-break: break-all;
  flex: 1;
}

.doc-icon { color: #ef4444; margin-right: 0.2rem; }

.badge-current {
  background: #d1fae5;
  color: #065f46;
  font-size: 0.65rem;
  font-weight: 700;
  padding: 0.1rem 0.35rem;
  border-radius: 8px;
  white-space: nowrap;
  flex-shrink: 0;
}

.badge-superseded {
  background: #f1f5f9;
  color: #64748b;
  font-size: 0.65rem;
  font-weight: 700;
  padding: 0.1rem 0.35rem;
  border-radius: 8px;
  white-space: nowrap;
  flex-shrink: 0;
}

.doc-item-meta {
  display: flex;
  align-items: center;
  gap: 0.4rem;
  flex-wrap: wrap;
  margin-bottom: 0.25rem;
}

.doc-date {
  font-size: 0.7rem;
  color: #6b7280;
  display: flex;
  align-items: center;
  gap: 0.25rem;
}

.doc-score-pill {
  font-size: 0.68rem;
  font-weight: 600;
  padding: 0.1rem 0.4rem;
  border-radius: 8px;
}
.score-good  { background: #d1fae5; color: #065f46; }
.score-warn  { background: #fef3c7; color: #92400e; }
.score-bad   { background: #fee2e2; color: #991b1b; }
.score-neutral { background: #f3f4f6; color: #6b7280; }

.doc-coverage-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 0.2rem;
}

.coverage-chip {
  background: #e0e7ff;
  color: #3730a3;
  font-size: 0.62rem;
  font-weight: 700;
  padding: 0.1rem 0.35rem;
  border-radius: 4px;
}

/* ── Session list items ── */
.session-item {
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  padding: 0.625rem 0.75rem;
  margin-bottom: 0.4rem;
  cursor: pointer;
  transition: all 0.15s;
}

.session-item:hover { border-color: #6366f1; background: #f5f3ff; }
.session-item.selected { border-color: #6366f1; background: #ede9fe; }

.session-item-top {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.4rem;
  margin-bottom: 0.25rem;
}

.session-date { font-size: 0.75rem; font-weight: 600; color: #374151; }

.session-status-badge {
  font-size: 0.65rem;
  font-weight: 700;
  padding: 0.1rem 0.35rem;
  border-radius: 8px;
  text-transform: uppercase;
}
.status-completed { background: #d1fae5; color: #065f46; }
.status-in_progress,
.status-pending   { background: #fef3c7; color: #92400e; }
.status-failed    { background: #fee2e2; color: #991b1b; }

.session-def-name {
  font-size: 0.72rem;
  color: #6b7280;
  margin-bottom: 0.3rem;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.session-summary {
  display: flex;
  align-items: center;
  gap: 0.4rem;
  flex-wrap: wrap;
}

.summary-pill {
  font-size: 0.7rem;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 0.2rem;
}
.summary-pill.passed { color: #059669; }
.summary-pill.failed { color: #dc2626; }
.summary-total { font-size: 0.7rem; color: #9ca3af; }

/* ── Detail panel ── */
.detail-content {
  display: flex;
  flex-direction: column;
  height: 100%;
  overflow: hidden;
}

.detail-summary-bar {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.625rem 1rem;
  background: #f8fafc;
  border-bottom: 1px solid #e5e7eb;
  flex-shrink: 0;
  flex-wrap: wrap;
}

.summary-card {
  display: flex;
  flex-direction: column;
  align-items: center;
  min-width: 60px;
}

.card-label {
  font-size: 0.65rem;
  color: #9ca3af;
  text-transform: uppercase;
  font-weight: 600;
  letter-spacing: 0.03em;
}

.card-value {
  font-size: 1.1rem;
  font-weight: 700;
  color: #374151;
}
.card-value.passed { color: #059669; }
.card-value.failed { color: #dc2626; }
.card-value.status-completed { color: #059669; }
.card-value.status-failed    { color: #dc2626; }

.summary-actions { margin-left: auto; }

.detail-split {
  display: flex;
  flex: 1;
  overflow: hidden;
}

.detail-pdf-pane {
  width: 45%;
  border-right: 1px solid #e5e7eb;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.detail-results-pane {
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.pane-label {
  font-size: 0.75rem;
  font-weight: 600;
  color: #6b7280;
  padding: 0.5rem 0.875rem;
  border-bottom: 1px solid #f3f4f6;
  display: flex;
  align-items: center;
  gap: 0.4rem;
  flex-shrink: 0;
}

.pane-label .fas { color: #6366f1; }

.pdf-wrapper { flex: 1; overflow: hidden; }
.pdf-iframe { width: 100%; height: 100%; border: none; display: block; }

.pdf-placeholder {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0.75rem;
  color: #9ca3af;
  font-size: 0.875rem;
}
.pdf-placeholder .fas { font-size: 2.5rem; color: #d1d5db; }

/* ── Requirement results ── */
.results-list {
  flex: 1;
  overflow-y: auto;
  padding: 0.75rem;
}

.results-loading,
.results-empty {
  padding: 2rem;
  text-align: center;
  color: #9ca3af;
  font-size: 0.875rem;
}

.results-section { margin-bottom: 1rem; }

.results-section-header {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.8rem;
  font-weight: 700;
  padding: 0.4rem 0.6rem;
  border-radius: 6px;
  margin-bottom: 0.5rem;
  user-select: none;
}

.failed-header { background: #fef2f2; color: #991b1b; }
.passed-header { background: #f0fdf4; color: #166534; }

.req-result-item {
  border-radius: 6px;
  padding: 0.6rem 0.75rem;
  margin-bottom: 0.35rem;
  font-size: 0.8rem;
}

.failed-item { background: #fff5f5; border-left: 3px solid #f87171; }
.passed-item { background: #f0fdf4; border-left: 3px solid #4ade80; }

.req-result-top {
  display: flex;
  align-items: center;
  gap: 0.4rem;
  margin-bottom: 0.2rem;
}

.req-code {
  background: #e0e7ff;
  color: #3730a3;
  font-size: 0.65rem;
  font-weight: 700;
  padding: 0.1rem 0.4rem;
  border-radius: 4px;
  font-family: monospace;
}

.severity-badge {
  font-size: 0.65rem;
  font-weight: 700;
  padding: 0.1rem 0.35rem;
  border-radius: 8px;
  text-transform: uppercase;
}
.sev-high   { background: #fee2e2; color: #991b1b; }
.sev-medium { background: #fef3c7; color: #92400e; }
.sev-low    { background: #e0e7ff; color: #3730a3; }

.req-result-name {
  color: #374151;
  font-weight: 500;
  margin-bottom: 0.2rem;
  line-height: 1.4;
}

.req-evidence {
  color: #6b7280;
  font-size: 0.75rem;
  line-height: 1.4;
}

/* ── Buttons ── */
.btn {
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  padding: 0.45rem 0.875rem;
  border-radius: 6px;
  font-size: 0.8rem;
  font-weight: 600;
  cursor: pointer;
  border: 1px solid transparent;
  transition: all 0.15s;
}
.btn-sm { padding: 0.3rem 0.6rem; font-size: 0.75rem; }
.btn-secondary { background: #f3f4f6; color: #374151; border-color: #d1d5db; }
.btn-secondary:hover { background: #e5e7eb; }
.btn-outline { background: transparent; color: #4f46e5; border-color: #6366f1; }
.btn-outline:hover { background: #eef2ff; }

.error-state { color: #dc2626; }

.embedded-docs-panel {
  display: block;
  position: static;
  background: none;
  padding: 0;
}

.embedded-docs-panel .docs-modal-container {
  width: 100%;
  max-width: 100%;
  max-height: none;
  box-shadow: none;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
}
</style>
